import { Component, Input, OnDestroy, OnChanges, AfterViewInit, ViewChild, SimpleChanges, OnInit, ChangeDetectorRef } from '@angular/core';
import { PatchManagerData, ToolsService } from 'tools';
import { Building, Campus, PatchManagerResultDto } from 'UvExplorerServer.Dtos.TS/Tools';
import { MessageService } from 'base';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NavigationService } from 'netview';
import { IdentityService } from 'identity';
import { DiscResultsService } from '../../../discovery';
import { Subscription } from 'rxjs';


@Component({
	selector: 'jack-to-switch-lookup',
	templateUrl: './jack-to-switch-lookup.component.html',
	styleUrls: ['./jack-to-switch-lookup.component.scss']
})
export class JackToSwitchLookupComponent implements OnInit {

	public form: UntypedFormGroup;

	public campusName: UntypedFormControl;
	public buildingName: UntypedFormControl;
	public jackLabel: UntypedFormControl;
	public doLive: UntypedFormControl;

	public jackRoom: string;
	public switchName: string;
	public ipAddress: string;
	public slotModule: string;
	public port: string;
	public ifIndex: string;
	public vlan: string;
	public connected: string;

	private allCampuses: Campus[];
	private allBuildings: Building[];

	public campusNames: string[];
	public buildingNames: string[];

	private networkLoadedSub: Subscription;
	private patchManagerDataLoadedSub: Subscription;

	constructor(private fb: UntypedFormBuilder, private toolsService: ToolsService,
		private messageService: MessageService, private navService: NavigationService,
		private identityService: IdentityService, private discResultsService: DiscResultsService,
		private changeDetector: ChangeDetectorRef) {

		let doLive = false;

		this.campusName = new UntypedFormControl("", Validators.required);
		this.buildingName = new UntypedFormControl("", Validators.required);
		this.jackLabel = new UntypedFormControl("", Validators.required);
		this.doLive = new UntypedFormControl(doLive);

		this.initResultFields();

		this.form = this.fb.group({
			campusName: this.campusName,
			buildingName: this.buildingName,
			jackLabel: this.jackLabel,
			doLive: this.doLive,
		});

		this.allCampuses = [];
		this.allBuildings = [];
		this.campusNames = [];
		this.buildingNames = [];
		
		this.networkLoadedSub = null;
		this.patchManagerDataLoadedSub = null;
	}

	private initResultFields(): void {
		this.jackRoom = "";
		this.switchName = "";
		this.ipAddress = "";
		this.slotModule = "";
		this.port = "";
		this.ifIndex = "";
		this.vlan = "";
		this.connected = "";
	}

	ngOnInit(): void {
		if (this.navService.isNetworkLoaded) {
			this.initData();
		}
		else {
			this.networkLoadedSub = this.discResultsService.onNetworkLoaded.subscribe(
				() => {
					this.networkLoadedSub.unsubscribe();
					this.networkLoadedSub = null;

					this.initData();
				},
				(error) => {
					this.messageService.displayErrorMessage("Could not retrieve Patch Manager data");
				});
		}
	}

	public ngOnDestroy(): void {
		if (this.networkLoadedSub) {
			this.networkLoadedSub.unsubscribe();
			this.networkLoadedSub = null;
		}
		if (this.patchManagerDataLoadedSub) {
			this.patchManagerDataLoadedSub.unsubscribe();
			this.patchManagerDataLoadedSub = null;
		}
	}

	private initData(): void {
		if (this.toolsService.hasPatchManagerData) {

			if (this.toolsService.patchManagerData) {
				this.allCampuses = [...this.toolsService.patchManagerData.campuses];
				this.allBuildings = [...this.toolsService.patchManagerData.buildings];
			}
			this.onGotData();
		}
		else {
			this.patchManagerDataLoadedSub = this.toolsService.patchManagerDataLoaded.subscribe(
				(patchManagerData: PatchManagerData) => {
					this.patchManagerDataLoadedSub.unsubscribe();
					this.patchManagerDataLoadedSub = null;

					this.allCampuses = [...patchManagerData.campuses];
					this.allBuildings = [...patchManagerData.buildings];

					this.onGotData();
				});
				
			this.toolsService.loadPatchManagerData();
		}
	}

	private onGotData(): void {

		this.campusNames = [];
		this.buildingNames = [];

		if (!!this.allCampuses && !!this.allBuildings) {

			this.allCampuses.forEach((campus: Campus) => {
				this.campusNames.push(campus.name);
			});

			let firstCampusName: string = ((this.campusNames.length > 0) ? this.campusNames[0] : "");
			this.campusName.setValue(firstCampusName);

			this.loadBuildingNames(firstCampusName);
		}
	}

	private loadBuildingNames(campusName: string): void {

		this.buildingNames = [];

		if (campusName) {
			let campusPrefix: string = campusName + ",";
			this.allBuildings.forEach((building: Building) => {
				if (building.name.startsWith(campusPrefix)) {
					let buildingName: string = building.name.substring(campusPrefix.length).trim();
					this.buildingNames.push(buildingName);
				}
			});
		}

		let firstBuildingName = ((this.buildingNames.length > 0) ? this.buildingNames[0] : "");
		this.buildingName.setValue(firstBuildingName);
	}

	public onCampusChanged(): void {
		this.loadBuildingNames(this.campusName.value);
	}

	public get enableLookup(): boolean {
		return (!!this.campusName.value && !!this.buildingName.value && !!this.jackLabel.value)
	}

	public lookup(): void {

		this.initResultFields();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.messageService.startWork();
		this.toolsService.lookupJackToSwitchPort(sessionGuid, netViewGuid, agentGuid, schedDiscGuid, schedDiscRsltGuid,
			this.campusName.value, this.buildingName.value, this.jackLabel.value, this.doLive.value).subscribe(
				(result: PatchManagerResultDto) => {
					this.messageService.endWork();
					this.jackRoom = result.jackToSwitchConnection.jack_room;
					this.switchName = result.jackToSwitchConnection.switch_name;
					this.slotModule = result.jackToSwitchConnection.switch_module_name;
					this.port = result.jackToSwitchConnection.switch_module_port_name;
					this.ifIndex = result.jackToSwitchConnection.ifIndex;
					this.ipAddress = result.ipAddress;
					this.vlan = result.vlan;
					this.connected = result.connected;

					this.changeDetector.detectChanges();
				},
				(error) => {
					this.messageService.endWork();
					this.messageService.displayErrorMessage("Jack was not found");
				});
	}

}
